import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { can, type Me } from '../auth/me';
import { warsawParts } from '../events/labels';
import { useUnsavedGuard } from '../ui/unsaved';

export const COMMENTS_RESOURCE = 'packlists.comments';

export type LineComment = {
  id: string;
  lineId: string;
  authorUserId: string;
  authorName: string;
  body: string;
  createdAt: string;
  editedAt: string | null;
  mine: boolean;
  notifiedNames: string[];
  version: number;
};
type CommentsView = { comments: LineComment[]; counts: Record<string, number> };
type Recipient = { userId: string; name: string; inCrew: boolean };

export const commentsKey = (eventId: string) => ['packlists', eventId, 'comments'] as const;

/** The comments of the event's packlist (one request for every line), when the user may see them. */
export function usePacklistComments(me: Me | null | undefined, eventId: string) {
  return useQuery({
    queryKey: commentsKey(eventId),
    queryFn: () => api<CommentsView>(`/api/packlists/${eventId}/comments`),
    enabled: !!me && can(me, COMMENTS_RESOURCE, 'view'),
  });
}

const when = (iso: string) => {
  const { day, time } = warsawParts(iso);
  const [y, m, d] = day.split('-');
  return `${Number(d)}.${m}.${y}, ${time}`;
};

/** The small speech bubble at a line's name: how many comments, opens the thread. */
export function CommentsButton({ name, count, open, onToggle }: { name: string; count: number; open: boolean; onToggle: () => void }) {
  return (
    <button
      className={`pl-comments-btn${count > 0 ? ' has' : ''}`}
      type="button"
      aria-label={`Komentarze: ${name}${count > 0 ? ` (${count})` : ''}`}
      aria-expanded={open}
      title={count > 0 ? `Komentarze (${count})` : 'Dodaj komentarz'}
      data-feature-id="packlists.comments"
      onClick={onToggle}
    >
      <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      </svg>
      {count > 0 && <span className="pl-comments-count">{count}</span>}
    </button>
  );
}

function CommentItem({ eventId, comment, me }: { eventId: string; comment: LineComment; me: Me }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(comment.body);
  const refresh = () => queryClient.invalidateQueries({ queryKey: commentsKey(eventId) });
  const edit = useMutation({
    mutationFn: () => api<LineComment>(`/api/packlists/comments/${comment.id}`, { method: 'PATCH', body: { body: text.trim(), version: comment.version } }),
    onSuccess: async () => {
      setEditing(false);
      await refresh();
    },
  });
  const remove = useMutation({ mutationFn: () => api(`/api/packlists/comments/${comment.id}`, { method: 'DELETE' }), onSuccess: refresh });
  const error = (edit.error ?? remove.error) instanceof ApiError ? ((edit.error ?? remove.error) as ApiError) : null;
  const canEdit = comment.mine && can(me, COMMENTS_RESOURCE, 'edit_own');
  const canDelete = comment.mine && can(me, COMMENTS_RESOURCE, 'delete_own');
  // owner 2026-10-23 21:14: typed and not saved: leaving asks first
  const editRef = useUnsavedGuard({
    dirty: editing && text.trim() !== comment.body.trim(),
    save: async () => {
      if (!text.trim()) return false;
      await edit.mutateAsync();
      return true;
    },
  });
  return (
    <li className="pl-comment">
      <div className="pl-comment-head">
        <strong>{comment.authorName}</strong>
        <span className="muted">
          {when(comment.createdAt)}
          {comment.editedAt && ' · edytowano'}
          {comment.notifiedNames.length > 0 && ` · powiadomiono: ${comment.notifiedNames.join(', ')}`}
        </span>
        {!editing && (canEdit || canDelete) && (
          <span className="pl-comment-actions">
            {canEdit && (
              <button className="btn small ghost" type="button" onClick={() => setEditing(true)}>
                Zmień
              </button>
            )}
            {canDelete && (
              <button className="btn small ghost danger" type="button" disabled={remove.isPending} onClick={() => remove.mutate()}>
                Usuń
              </button>
            )}
          </span>
        )}
      </div>
      {editing ? (
        <form
          ref={editRef}
          className="pl-comment-form"
          aria-label="Zmień komentarz"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) edit.mutate();
          }}
        >
          <textarea aria-label="Treść komentarza" rows={2} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} />
          <span className="pl-comment-form-actions">
            <button className="btn small primary" type="submit" disabled={edit.isPending || !text.trim()}>
              Zapisz
            </button>
            <button
              className="btn small ghost"
              type="button"
              onClick={() => {
                setEditing(false);
                setText(comment.body);
                edit.reset();
              }}
            >
              Anuluj
            </button>
          </span>
        </form>
      ) : (
        <p className="pl-comment-body">{comment.body}</p>
      )}
      {error && (
        <p className="alert" role="alert">
          {error.status === 409 ? 'Ktoś już zmienił ten komentarz. Odśwież i spróbuj ponownie.' : error.message}
        </p>
      )}
    </li>
  );
}

/**
 * The thread of one line (owner remark 4/38): author, time, text; my own comments can be changed and deleted (no
 * "are you sure", as everywhere). A new comment can notify chosen people — the crew of the event first, then anyone
 * else who may see this packlist's comments — with a bell notification that opens this line.
 */
export function LineComments({ eventId, lineId, name, comments, me }: { eventId: string; lineId: string; name: string; comments: LineComment[]; me: Me }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [notify, setNotify] = useState<string[]>([]);
  const [choosing, setChoosing] = useState(false);
  const canCreate = can(me, COMMENTS_RESOURCE, 'create');
  const recipients = useQuery({
    queryKey: ['packlists', eventId, 'comment-recipients'],
    queryFn: () => api<{ recipients: Recipient[] }>(`/api/packlists/${eventId}/comment-recipients`),
    enabled: canCreate && choosing,
    staleTime: 60_000,
  });
  const add = useMutation({
    mutationFn: () => api<LineComment>(`/api/packlists/lines/${lineId}/comments`, { method: 'POST', body: { body: text.trim(), notifyUserIds: notify } }),
    onSuccess: async () => {
      setText('');
      setNotify([]);
      setChoosing(false);
      await queryClient.invalidateQueries({ queryKey: commentsKey(eventId) });
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    if (text.trim() && !add.isPending) add.mutate();
  }
  const addRef = useUnsavedGuard({
    dirty: canCreate && text.trim() !== '',
    save: async () => {
      await add.mutateAsync();
      return true;
    },
  });
  const toggle = (id: string) => setNotify(notify.includes(id) ? notify.filter((x) => x !== id) : [...notify, id]);
  const list = recipients.data?.recipients ?? [];
  return (
    <section className="pl-comments" aria-label={`Komentarze: ${name}`} data-feature-id="packlists.comments">
      {comments.length === 0 ? (
        <p className="muted pl-comments-empty">Brak komentarzy.</p>
      ) : (
        <ul className="pl-comment-list">
          {comments.map((c) => (
            <CommentItem key={`${c.id}-${c.version}`} eventId={eventId} comment={c} me={me} />
          ))}
        </ul>
      )}
      {canCreate && (
        <form ref={addRef} className="pl-comment-form" aria-label="Nowy komentarz" onSubmit={submit}>
          <textarea
            aria-label="Nowy komentarz"
            rows={2}
            maxLength={2000}
            placeholder="Napisz komentarz do tej pozycji…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              // ⌘/Ctrl+Enter adds (Enter alone breaks the line)
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(e);
            }}
          />
          <div className="pl-notify">
            {!choosing ? (
              <button className="btn small ghost" type="button" onClick={() => setChoosing(true)}>
                Powiadom osoby…
              </button>
            ) : (
              <fieldset className="pl-notify-list" aria-label="Powiadom">
                <legend>Powiadom (dzwonek z linkiem do tej pozycji):</legend>
                {recipients.isLoading && <span className="muted">Wczytywanie…</span>}
                {!recipients.isLoading && list.length === 0 && <span className="muted">Nikt inny nie widzi komentarzy tej sprzętówki.</span>}
                {list.map((r) => (
                  <label key={r.userId} className="pl-notify-person">
                    <input type="checkbox" checked={notify.includes(r.userId)} onChange={() => toggle(r.userId)} />
                    {r.name}
                    {r.inCrew && <span className="chip">obsada</span>}
                  </label>
                ))}
              </fieldset>
            )}
            <button className="btn small primary" type="submit" disabled={add.isPending || !text.trim()}>
              {notify.length > 0 ? `Dodaj i powiadom (${notify.length})` : 'Dodaj komentarz'}
            </button>
          </div>
          {add.error instanceof ApiError && (
            <p className="alert" role="alert">
              {add.error.message}
            </p>
          )}
        </form>
      )}
    </section>
  );
}
